import { useEffect, useMemo, useRef, useState } from 'react';
import { useLang } from '../../lib/language';

/**
 * LookupCombo — searchable single-select over a lookup table, with an inline
 * "add what I just typed" row.
 *
 * Built for the KSA address layout's المدينة (GL_Governorate) and الحي
 * (GL_Area) pickers: those tables are per-tenant reference data that is never
 * complete, and a cashier who can't find a district must not be stuck. When
 * the typed text matches nothing (case- and whitespace-insensitive, either
 * script), the last row of the menu becomes "+ Add «text»", which calls
 * `onCreate` and selects whatever comes back.
 *
 * Deliberately reuses the `.area-picker*` classes from AreaPicker so both
 * comboboxes in the same form look identical — only the create row is new.
 */

export interface LookupOption {
  id: number;
  name: string | null;
  name_ar: string | null;
}

export function LookupCombo({
  value,
  options,
  onChange,
  onCreate,
  disabled,
  loading,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  createLabel,
}: {
  value: number | null;
  options: LookupOption[];
  onChange: (id: number | null) => void;
  /** Create the missing entry and return it (already persisted). */
  onCreate?: (name: string) => Promise<LookupOption | null>;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  /** Rendered as `${createLabel} "typed"`. Defaults to "+ Add". */
  createLabel?: string;
}) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click so the popover never traps focus.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setFilter('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const isArabic = isRtlOrArabic();
  const selected = useMemo(() => options.find((o) => o.id === value) ?? null, [options, value]);

  const needle = filter.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!needle) return options;
    return options.filter((o) =>
      `${o.name ?? ''} ${o.name_ar ?? ''}`.toLowerCase().includes(needle),
    );
  }, [options, needle]);

  // Offer the create row only when the typed text isn't already an entry —
  // otherwise a cashier types an existing district and gets a second copy.
  const exactExists = useMemo(
    () =>
      options.some(
        (o) =>
          (o.name ?? '').trim().toLowerCase() === needle ||
          (o.name_ar ?? '').trim().toLowerCase() === needle,
      ),
    [options, needle],
  );
  const canCreate = Boolean(onCreate) && needle.length > 0 && !exactExists && !creating;

  function primary(o: LookupOption | null): string {
    if (!o) return placeholder ?? t({ en: 'Select', ar: 'اختر' });
    const p = isArabic ? o.name_ar : o.name;
    return (p ?? o.name ?? o.name_ar ?? `#${o.id}`).trim();
  }

  function secondary(o: LookupOption): string | null {
    const other = (isArabic ? o.name : o.name_ar)?.trim();
    if (!other) return null;
    return other === primary(o) ? null : other;
  }

  async function create() {
    if (!onCreate) return;
    const name = filter.trim();
    if (!name) return;
    setCreating(true);
    try {
      const created = await onCreate(name);
      if (created) {
        onChange(created.id);
        setOpen(false);
        setFilter('');
      }
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className={`area-picker ${open ? 'open' : ''}`} ref={wrapperRef}>
      <button
        type="button"
        className="area-picker-trigger"
        disabled={disabled || loading}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={selected ? 'has-value' : 'placeholder'}>
          {loading ? t({ en: 'Loading…', ar: 'جارٍ التحميل…' }) : primary(selected)}
        </span>
        <span className="caret" aria-hidden>▾</span>
      </button>
      {selected && !disabled && (
        <button
          type="button"
          className="area-picker-clear"
          aria-label={t({ en: 'Clear', ar: 'مسح' })}
          onClick={(e) => {
            e.stopPropagation();
            onChange(null);
          }}
        >
          ×
        </button>
      )}
      {open && !loading && (
        <div className="area-picker-menu" role="listbox">
          <input
            className="area-picker-search"
            placeholder={searchPlaceholder ?? t({ en: 'Search or type a new name…', ar: 'ابحث أو اكتب اسمًا جديدًا…' })}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => {
              // Enter picks the only match, or creates when there is none.
              if (e.key !== 'Enter') return;
              e.preventDefault();
              if (filtered.length === 1 && filtered[0]) {
                onChange(filtered[0].id);
                setOpen(false);
                setFilter('');
              } else if (canCreate) {
                void create();
              }
            }}
            autoFocus
          />
          <ul className="area-picker-list">
            {filtered.slice(0, 200).map((o) => {
              const alt = secondary(o);
              return (
                <li
                  key={o.id}
                  role="option"
                  aria-selected={o.id === value}
                  className={`area-picker-option ${o.id === value ? 'selected' : ''}`}
                  onClick={() => {
                    onChange(o.id);
                    setOpen(false);
                    setFilter('');
                  }}
                >
                  <div className="area-name">{primary(o)}</div>
                  {alt && <div className="area-name-alt">{alt}</div>}
                </li>
              );
            })}
            {filtered.length === 0 && !canCreate && (
              <li className="area-picker-empty">
                {creating
                  ? t({ en: 'Adding…', ar: 'جارٍ الإضافة…' })
                  : emptyLabel ?? t({ en: 'No matches', ar: 'لا توجد نتائج' })}
              </li>
            )}
            {filtered.length > 200 && (
              <li className="area-picker-hint">
                {t({
                  en: `${filtered.length - 200} more — refine your search`,
                  ar: `${filtered.length - 200} أخرى — نقّح بحثك`,
                })}
              </li>
            )}
          </ul>
          {canCreate && (
            <button type="button" className="area-picker-create" onClick={() => void create()}>
              <span className="area-picker-create-plus" aria-hidden>+</span>
              <span>
                {createLabel ?? t({ en: 'Add', ar: 'إضافة' })} “{filter.trim()}”
              </span>
            </button>
          )}
          {creating && (
            <div className="area-picker-hint">{t({ en: 'Adding…', ar: 'جارٍ الإضافة…' })}</div>
          )}
        </div>
      )}
    </div>
  );
}

function isRtlOrArabic(): boolean {
  if (typeof document === 'undefined') return false;
  const root = document.documentElement;
  return root.dir === 'rtl' || root.lang === 'ar' || root.lang?.startsWith('ar-');
}
